/**
 * The two hours on site, the four jobs a day, and two scheduling rules.
 *
 * ## Why a second clock, when there is already an SLA
 *
 * The SLA clock answers the customer's question: how long from reporting a
 * fault until somebody fixes it. It runs from when the ticket was raised, and
 * it pauses while the business is waiting on the customer, on parts or on
 * access — because none of that is the customer's time to be charged for.
 *
 * This clock answers a different question, which is the business's: once a
 * technician is standing at the site, how long does the work take. It starts
 * when they mark themselves on site and it does not pause, because from that
 * moment the constraint is the technician's day, not the customer's patience.
 *
 * Two clocks measuring two things. Collapsing them would break both: a visit
 * paused for parts would keep burning a technician's allowance, and a fast
 * visit on a ticket that waited a week would look like a breach.
 *
 * ## Why two hours, and why four a day
 *
 * They are the same number seen twice. Four jobs at two hours is an eight-hour
 * day, which is the arithmetic the target comes from and the reason neither
 * figure can be changed without the other. A five-job target at two hours is a
 * ten-hour day nobody agreed to.
 *
 * ## Why exceeding it asks for a reason rather than failing
 *
 * A visit that runs long is usually a good reason — a splice that would not
 * take, a pole that needed KPLC — and occasionally a bad one. The system cannot
 * tell the difference and should not pretend to. What it can do is make sure
 * the difference is written down at the moment somebody knows it, which is
 * worth more than a red number on a report six weeks later.
 */
/** Minutes on site before a visit is overrunning. */
export const ONSITE_ALLOWANCE_MIN = 120;
/** Jobs a technician is expected to complete on a normal working day. */
export const DAILY_TICKET_TARGET = 4;
/**
 * Statuses that mean a technician is on site with the clock running.
 *
 * `EN_ROUTE` is absent deliberately: travel is not the two hours. A technician
 * stuck behind a lorry on the Kisii–Keroka road is not overrunning a visit they
 * have not started.
 */
export const ONSITE_STATUSES = ['ON_SITE', 'IN_PROGRESS'];
/** When the allowance runs out for a visit that began at `arrivedAt`. */
export function onSiteDueAt(arrivedAt) {
    return new Date(arrivedAt.getTime() + ONSITE_ALLOWANCE_MIN * 60_000);
}
/** Minutes spent on site so far. */
export function minutesOnSite(arrivedAt, now = new Date()) {
    return Math.max(0, Math.round((now.getTime() - arrivedAt.getTime()) / 60_000));
}
export function onSiteState(arrivedAt, now = new Date()) {
    const minutes = minutesOnSite(arrivedAt, now);
    const remaining = ONSITE_ALLOWANCE_MIN - minutes;
    return {
        minutes,
        remaining,
        percent: Math.round((minutes / ONSITE_ALLOWANCE_MIN) * 100),
        overrunning: remaining < 0,
        nearlyOver: remaining >= 0 && remaining <= ONSITE_ALLOWANCE_MIN * 0.25,
    };
}
/**
 * Whether closing this visit has to come with an explanation.
 *
 * Asked of the elapsed time rather than of a stored flag, so a visit that
 * overran and was closed while the sweep was between ticks still asks.
 */
export function needsOverrunReason(arrivedAt, closedAt = new Date()) {
    if (!arrivedAt)
        return false;
    return minutesOnSite(arrivedAt, closedAt) > ONSITE_ALLOWANCE_MIN;
}
/** The shortest explanation worth recording. Anything less is "asdf". */
export const OVERRUN_REASON_MIN_LENGTH = 15;
export function refuseOverrunReason(reason) {
    const text = (reason ?? '').trim();
    if (text.length === 0) {
        return 'This visit ran past two hours. Say briefly what held it up.';
    }
    if (text.length < OVERRUN_REASON_MIN_LENGTH) {
        return 'A few more words, please — enough that somebody reading it next month understands.';
    }
    return null;
}
/* ── Scheduling rules ─────────────────────────────────────────────────────── */
/**
 * Work that is always the highest priority the moment it is raised.
 *
 * A new connection is revenue that has not started yet, and a customer who has
 * paid and is waiting is the least patient customer this business has. It was
 * P3, which put it behind every slow-connection complaint.
 *
 * Applied at creation only. A dispatcher who deliberately drops one to P2
 * because the customer asked for next week should not have the system quietly
 * put it back.
 */
export const ALWAYS_URGENT_TYPES = ['NEW_INSTALL'];
export function priorityFor(jobTypeCode, fallback) {
    return ALWAYS_URGENT_TYPES.includes(jobTypeCode) ? 'P1' : fallback;
}
/**
 * Work that belongs in the afternoon.
 *
 * A FAT install means opening a splitter enclosure that live customers hang
 * off. Doing it in the afternoon means the disruption lands when most people
 * are at work rather than during the morning peak — and it leaves the rest of
 * the day for the fault calls it might cause.
 *
 * `FAT_FAULT` is deliberately not here. A broken splitter is customers already
 * off, and telling a dispatcher to wait until noon would be absurd.
 */
export const AFTERNOON_TYPES = ['FAT_INSTALL'];
/** Noon. The line between morning and afternoon, in the org's own timezone. */
export const AFTERNOON_FROM_HOUR = 12;
/**
 * Why this is the wrong time for this work, or null.
 *
 * A warning rather than a refusal. The rule is right almost always and there
 * will be a morning when a landlord is only available at nine, and a system
 * that cannot be overruled by somebody who knows more than it does is a system
 * people route around entirely.
 */
export function warnAboutSchedule(jobTypeCode, scheduledFor, hourInOrgZone) {
    if (!scheduledFor)
        return null;
    if (!AFTERNOON_TYPES.includes(jobTypeCode))
        return null;
    const hour = hourInOrgZone(scheduledFor);
    if (hour >= AFTERNOON_FROM_HOUR)
        return null;
    return 'FAT installs are done in the afternoon, so the disruption misses the morning peak. Schedule it after noon unless there is a reason not to.';
}
/**
 * How a technician did against the target.
 *
 * Measured per **working day in the window**, not per calendar day, or every
 * figure would be four-sevenths of the truth and a technician who worked a
 * perfect week would appear to be missing the target by a third.
 */
export function dailyTarget(completed, workingDays) {
    const days = Math.max(workingDays, 1);
    const perDay = Math.round((completed / days) * 10) / 10;
    return {
        completed,
        target: DAILY_TICKET_TARGET,
        met: perDay >= DAILY_TICKET_TARGET,
        perDay,
    };
}
//# sourceMappingURL=onsite.js.map